import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import api from "@/lib/api";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { SettingsProvider } from "@/context/SettingsContext";

import StoreLayout from "@/components/store/StoreLayout";
import VipGate from "@/components/store/VipGate";
import PageTracker from "@/components/store/PageTracker";
import TrackOrder from "@/pages/TrackOrder";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Account from "@/pages/Account";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Affiliates from "@/pages/Affiliates";
import AffiliateDashboard from "@/pages/AffiliateDashboard";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Journal from "@/pages/Journal";
import JournalPost from "@/pages/JournalPost";
import Lookbook from "@/pages/Lookbook";
import OurStory from "@/pages/OurStory";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminMarketing from "@/pages/admin/AdminMarketing";
import AdminReports from "@/pages/admin/AdminReports";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminPageImages from "@/pages/admin/AdminPageImages";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminQRLabels from "@/pages/admin/AdminQRLabels";
import AdminAgencyFees from "@/pages/admin/AdminAgencyFees";
import AdminAffiliates from "@/pages/admin/AdminAffiliates";
import AdminCMS from "@/pages/admin/AdminCMS";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminGuide from "@/pages/admin/AdminGuide";
import AdminGuideEditor from "@/pages/admin/AdminGuideEditor";
import AdminOurStory from "@/pages/admin/AdminOurStory";
import AdminBrand from "@/pages/admin/AdminBrand";
import AdminActivity from "@/pages/admin/AdminActivity";

const S = "super_admin", M = "store_manager", F = "fulfillment_staff";

function RoleRoute({ allow, children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center font-heading text-3xl text-ink/40">Loading…</div>;
  if (!user || !allow.includes(user.role)) return <Navigate to="/admin" replace />;
  return children;
}

function RefCapture() {
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) { localStorage.setItem("sv_ref", ref); api.post(`/affiliates/track/${ref}`).catch(() => {}); }
  }, []);
  return null;
}

function App() {
  return (
    <SettingsProvider>
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
            <BrowserRouter>
              <RefCapture />
              <PageTracker />
              <Toaster position="top-center" toastOptions={{ style: { borderRadius: 0, fontFamily: "Montserrat" } }} />
              <Routes>
                <Route element={<VipGate><StoreLayout /></VipGate>}>
                  <Route path="/" element={<Home />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/journal" element={<Journal />} />
                  <Route path="/journal/:slug" element={<JournalPost />} />
                  <Route path="/lookbook" element={<Lookbook />} />
                  <Route path="/our-story" element={<OurStory />} />
                  <Route path="/product/:slug" element={<ProductDetail />} />
                  <Route path="/track/:orderNumber" element={<TrackOrder />} />
                  <Route path="/account" element={<Account />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/affiliates" element={<Affiliates />} />
                  <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />
                </Route>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />

                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="products" element={<RoleRoute allow={[S, M]}><AdminProducts /></RoleRoute>} />
                  <Route path="categories" element={<RoleRoute allow={[S, M]}><AdminCategories /></RoleRoute>} />
                  <Route path="media" element={<RoleRoute allow={[S, M]}><AdminMedia /></RoleRoute>} />
                  <Route path="marketing" element={<RoleRoute allow={[S, M]}><AdminMarketing /></RoleRoute>} />
                  <Route path="reports" element={<RoleRoute allow={[S, M]}><AdminReports /></RoleRoute>} />
                  <Route path="analytics" element={<RoleRoute allow={[S, M]}><AdminAnalytics /></RoleRoute>} />
                  <Route path="agency-fees" element={<RoleRoute allow={[S]}><AdminAgencyFees /></RoleRoute>} />
                  <Route path="affiliates" element={<RoleRoute allow={[S]}><AdminAffiliates /></RoleRoute>} />
                  <Route path="users" element={<RoleRoute allow={[S]}><AdminUsers /></RoleRoute>} />
                  <Route path="cms" element={<RoleRoute allow={[S]}><AdminCMS /></RoleRoute>} />
                  <Route path="page-images" element={<RoleRoute allow={[S]}><AdminPageImages /></RoleRoute>} />
                  <Route path="blog" element={<RoleRoute allow={[S, M]}><AdminBlog /></RoleRoute>} />
                  <Route path="reviews" element={<RoleRoute allow={[S, M]}><AdminReviews /></RoleRoute>} />
                  <Route path="qr-labels" element={<RoleRoute allow={[S, M]}><AdminQRLabels /></RoleRoute>} />
                  <Route path="settings" element={<RoleRoute allow={[S]}><AdminSettings /></RoleRoute>} />
                  <Route path="guide" element={<AdminGuide />} />
                  <Route path="guide-editor" element={<RoleRoute allow={[S]}><AdminGuideEditor /></RoleRoute>} />
                  <Route path="our-story" element={<RoleRoute allow={[S]}><AdminOurStory /></RoleRoute>} />
                  <Route path="brand" element={<RoleRoute allow={[S]}><AdminBrand /></RoleRoute>} />
                  <Route path="activity" element={<RoleRoute allow={[S]}><AdminActivity /></RoleRoute>} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </SettingsProvider>
  );
}

export default App;
